import { createInterfaceId, Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { EventEmitterImpl, type Event } from '@gitlab-org/core';
import type { Disposable } from '@gitlab-org/disposable';
import { CliBackend } from '../backend/backend';
import { BackendFactory } from '../backend/backend_factory';
import { formatSessionLogMarker } from './session_log_marker';
import { ChatSession, Session } from './session';
import { SessionDataService } from './session_data_service';
import type { GetSessionHistoryOptions, SessionHistoryPage } from './session_history';

export interface CreateSessionResult {
  session: Session;
  sessionDetails: SessionDetails;
}

interface SessionDetails {
  sessionRejectionReason?: string;
}

export interface SessionManager extends Disposable {
  createSession(
    existingSessionId?: string,
    opts?: { skipHistoryRehydration?: boolean },
  ): Promise<CreateSessionResult>;
  getActiveSession(): Session | undefined;
  getSessionHistory(options: GetSessionHistoryOptions): Promise<SessionHistoryPage>;
  switchToSession(sessionId: string): Promise<Session>;
  onActiveSessionChanged: Event<Session>;
}

export const SessionManager = createInterfaceId<SessionManager>('SessionManager');

@Implements(SessionManager)
@Service({
  dependencies: [Logger, BackendFactory, SessionDataService],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultSessionManager implements SessionManager {
  #sessions = new Map<string, Session>();

  #activeSessionId?: string;

  #activeBackend?: CliBackend;

  #sessionDataService: SessionDataService;

  #logger: Logger;

  #backendFactory: BackendFactory;

  #activeSessionChangedEmitter = new EventEmitterImpl<Session>();

  onActiveSessionChanged = this.#activeSessionChangedEmitter.event;

  constructor(
    logger: Logger,
    backendFactory: BackendFactory,
    sessionDataService: SessionDataService,
  ) {
    this.#logger = withPrefix(logger, '[SessionManager]');
    this.#backendFactory = backendFactory;
    this.#sessionDataService = sessionDataService;
  }

  async createSession(
    existingSessionId?: string,
    opts: { skipHistoryRehydration?: boolean } = {},
  ): Promise<CreateSessionResult> {
    const { skipHistoryRehydration = false } = opts;
    const result = await this.#createAndActivateSession(existingSessionId);

    if (existingSessionId && !skipHistoryRehydration && result.session.sessionId) {
      result.session.addInfo('Restoring session...');
      this.#activeSessionChangedEmitter.fire(result.session);
      await this.#rehydrateActiveSession(result.session.sessionId, result.session);
    }

    this.#activeSessionChangedEmitter.fire(result.session);
    return result;
  }

  async #createAndActivateSession(existingSessionId?: string): Promise<CreateSessionResult> {
    this.#activeBackend?.dispose();
    // The resume id is threaded through create() so runtime session switching
    // can supply an arbitrary id.
    const backend = this.#backendFactory.create(existingSessionId);
    this.#activeBackend = backend;
    const { sessionId, sessionRejectionReason } = await backend.initialize();

    const session = new ChatSession(sessionId, backend, this.#logger);

    this.#sessions.set(session.sessionId, session);
    this.#activeSessionId = session.sessionId;

    // Logged at `info` (not `debug`) so it survives a reduced `--log-level`
    // and remains a reliable anchor for `duo log session-id <id>`.
    this.#logger.info(formatSessionLogMarker(session.sessionId));
    return { session, sessionDetails: { sessionRejectionReason } };
  }

  getActiveSession(): Session | undefined {
    if (this.#activeSessionId === undefined) return undefined;
    return this.#sessions.get(this.#activeSessionId);
  }

  async getSessionHistory(options: GetSessionHistoryOptions): Promise<SessionHistoryPage> {
    const search = options.search?.trim() || undefined;
    return this.#sessionDataService.getSessionHistory({ ...options, search });
  }

  async switchToSession(sessionId: string): Promise<Session> {
    this.#cancelActiveSessionIfLoading();
    const { session } = await this.#createAndActivateSession(sessionId);
    await this.#rehydrateActiveSession(sessionId, session);
    this.#activeSessionChangedEmitter.fire(session);
    return session;
  }

  async #rehydrateActiveSession(sessionId: string, session: Session): Promise<void> {
    const events = await this.#sessionDataService.getSessionMessages(sessionId);
    session.rehydrateFromEvents(events);
  }

  dispose(): void {
    this.#activeSessionChangedEmitter.dispose();
  }

  #cancelActiveSessionIfLoading(): void {
    const activeSession = this.getActiveSession();
    if (activeSession?.isLoading) {
      this.#logger.info(
        `Cancelling active session "${activeSession.sessionId}" before switching session.`,
      );
      activeSession.cancelStream();
    }
  }
}
